import axios from 'axios'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Button} from 'react-bootstrap'
import {Link, useLocation} from 'react-router-dom'
import {BASE_URL} from '../config'
import {useAuth} from '../auth'
import useSenderIdsOptions, {ISenderIdsOptionsArray} from '../../hooks/useSenderIdsOptions'
import toast from 'react-hot-toast'
import {ISenderId, ISenderIdArray} from '../SMSBroadCast/core/_model'
import {Helmet} from 'react-helmet-async'
import ListSkeleton from '../../utils/components/Skeleton/ListSkeleton'
import {VirtualizedDualListBox} from './VirtualizedDualListBox'

const GET_USER_SENDER_ID = `${BASE_URL}/api/protect/getallSenders`
const UPDATE_USER_SENDER_ID = `${BASE_URL}/api/protect/updateSender`

export const UpdateSenderId = () => {
  const {auth} = useAuth()
  const {state: updateUser} = useLocation()
  const {senderIdsOptions, allDefaultIds, isLoading} = useSenderIdsOptions()

  const [selected, setSelected] = useState<string[]>([])

  // Load the user's current sender selections
  useEffect(() => {
    const controller = new AbortController()

    const getSenders = async () => {
      try {
        const response = await axios.get(`${GET_USER_SENDER_ID}/${updateUser.id}`, {
          headers: {Authorization: `Bearer ${auth?.accessToken}`},
          signal: controller.signal,
        })
        const data: ISenderIdArray = response.data
        setSelected(data.map((id) => id.sender))
      } catch (error) {
        if (!axios.isCancel(error)) console.log(error)
      }
    }

    getSenders()
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateUser.id])

  // Derived instead of an effect+setState pair -> one fewer render, no stale state window
  const filteredSenders: ISenderIdsOptionsArray = useMemo(() => {
    if (!senderIdsOptions.length) return []
    const filterValue = updateUser.isP2p === '1' ? 2 : 0
    return senderIdsOptions.filter((sender) => sender.branded === filterValue)
  }, [senderIdsOptions, updateUser.isP2p])

  const handleUpdateClick = useCallback(async () => {
    // Set lookup instead of selected.includes(...) inside .filter -> O(n) instead of O(n*m)
    const selectedSet = new Set(selected)
    const senderInfo = allDefaultIds.filter((item: ISenderId) => selectedSet.has(item.sender))

    const toastId = toast.loading('Updating..')
    try {
      const response = await axios.put(
        `${UPDATE_USER_SENDER_ID}/${updateUser.id}`,
        {senderInfo},
        {headers: {Authorization: `Bearer ${auth?.accessToken}`}}
      )
      toast.success('Updated successfully', {id: toastId})
      return response
    } catch (error: any) {
      console.log(error)
      toast.error(`Error: ${error.message}`, {id: toastId})
    }
  }, [selected, allDefaultIds, updateUser.id, auth?.accessToken])

  return (
    <>
      <Helmet>
        <title>User - Update Sender ID</title>
      </Helmet>
      <div className='card mb-5 mb-xl-10'>
        <div className='card-header border-0 cursor-pointer' role='button'>
          <div className='card-title m-0'>
            <h3 className='er m-0'>Update User Sender ID</h3>
          </div>
        </div>

        {isLoading ? (
          <ListSkeleton />
        ) : (
          <>
            <div className='card-body'>
              <div className='row mb-4'>
                <label className='col-lg-4 col-form-label fs-6'>
                  <span>User ID</span>
                </label>
                <div className='col-lg-8 fv-row'>
                  <input
                    name='user_id'
                    type='text'
                    placeholder='User ID'
                    className='form-control form-control-lg custom-input'
                    defaultValue={updateUser?.username}
                    disabled
                  />
                </div>
              </div>

              <div className='row mb-4'>
                <label className='col-lg-4 col-form-label fs-6'>
                  <span>Username</span>
                </label>
                <div className='col-lg-8 fv-row'>
                  <input
                    name='username'
                    placeholder='Username'
                    type='text'
                    className='form-control form-control-lg custom-input'
                    defaultValue={updateUser?.user_full_name}
                    disabled
                  />
                </div>
              </div>

              <VirtualizedDualListBox
                options={filteredSenders}
                selected={selected}
                onChange={setSelected}
                // NOTE: adjust these two accessors to match the real field
                // names on ISenderIdsOption from useSenderIdsOptions — I
                // don't have that interface's source, so this assumes the
                // common react-dual-listbox `{ value, label }` shape.
                // If ISenderIdsOption instead uses e.g. `sender`, change
                // both lines to `(o) => o.sender`.
                getOptionValue={(o) => (o as any).value ?? (o as any).sender}
                getOptionLabel={(o) => (o as any).label ?? (o as any).sender ?? (o as any).value}
              />
            </div>

            <div className='card-footer'>
              <div className='d-flex gap-5'>
                <Link to='/users/userlist'>
                  <Button variant='secondary'>Cancel</Button>
                </Link>
                <Button variant='primary' onClick={handleUpdateClick}>
                  Update Sender ID
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}
